import { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { ActionType, ComponentType, logEvent, markStep } from '@cbhq/client-analytics';

import { RemoveMemberAlert } from '../components/RemoveMemberAlert';
import { useTeamApi } from '../useTeamApi';
import type { Member } from '../utils/constants';
import { getDisplayName } from '../utils/helpers';

type RemoveMemberProps = {
  visible: boolean;
  member: Member;
  organizationId: string;
  onClose: () => void;
  onSuccess?: () => void;
};

const messages = defineMessages({
  removeMemberModalSuccess: {
    id: 'Team.removeMemberModalSuccess',
    defaultMessage: 'Member removed',
    description: 'Remove member modal success',
  },
  removeMemberModalError: {
    id: 'Team.removeMemberModalError',
    defaultMessage: 'Error removing member',
    description: 'Remove member modal error',
  },
});

export function RemoveMember({
  visible,
  member,
  organizationId,
  onClose,
  onSuccess,
}: RemoveMemberProps) {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const { removeMemberFromOrg } = useTeamApi(organizationId);

  useEffect(() => {
    if (!visible) {
      return;
    }

    markStep('loaded_workspace_remove_member_modal');
  }, [visible]);

  const handleRemoveMember = useCallback(async () => {
    logEvent('member_removed_confirmed', {
      componentType: ComponentType.button,
      action: ActionType.click,
    });

    try {
      markStep('submit_workspace_member_removal');
      await removeMemberFromOrg({ memberUuid: member.userUuid });
      logEvent('member_removed_success', {
        componentType: ComponentType.api,
        action: ActionType.api,
      });
      markStep('workspace_member_removal_success');
      toast.show(formatMessage(messages.removeMemberModalSuccess));
      onClose();
      onSuccess?.();
    } catch (error) {
      toast.show(formatMessage(messages.removeMemberModalError));
      logEvent('member_remove_failed', {
        componentType: ComponentType.api,
        action: ActionType.api,
      });
    }
  }, [removeMemberFromOrg, member, onClose, onSuccess, toast, formatMessage]);

  return (
    <RemoveMemberAlert
      visible={visible}
      onClose={onClose}
      memberName={getDisplayName(member)}
      onConfirm={handleRemoveMember}
    />
  );
}
